/*global SVP */
'use strict';

/**
 * The splash plugin shows an overlay with a play button or loading indicator,
 * depending on the current state of the video player. In addition, the plugin
 * will show an indication on whether the current media is a live stream.
 */

import Sizzle from 'sizzle';
import * as domUtils from '../util/dom';
const config = require('../config');
import videoAttributesUtil from '../util/video-attributes';
import debug from '../util/debug';

const NAMESPACE = 'splash';
const Debug = debug(NAMESPACE);

const BUTTON_ICONS = {
    play: 'svp_splash__btn-play',
    loading: 'svp_splash__btn-loading'
};

const ATTRIBUTES = {
    videoIsLive: 'svp_is--video-live',
    splashTextVisible: 'svp_is--splash-text-visible'
};

const CLASSNAMES = {
    playButton: 'svp_js-splash--btn-play',
    liveIcon: 'svp_js-splash--live-icon',
    splash: 'svp_js-splash',
    splashText: 'svp_js-splash--text'
};

const OVERRIDE_CLASSNAMES = {
    playButton: 'svp_css-splash--btn-play'
};

const STATE_CLASS = {
    hidden: 'svp_is--hidden'
};

/**
 * Status enum for the splash plugin state.
 *
 * @readonly
 * @enum {string}
 */
const STATUS = {
    /**
     * Indicates that the splash should show the play button.
     */
    idle: 'idle',

    /** Indicates that the video is currently loading or buffering. */
    loading: 'loading',

    /** Indicates that the splash is hidden. */
    hidden: 'hidden'
};

function createSplashElement() {
    const splashElement = domUtils.fromString(
        '<div class="svp_splash ' + CLASSNAMES.splash + '">\
            <div class="svp_splash__video-live ' + CLASSNAMES.liveIcon + '">\
                <span class="svp_splash__video-live__icon"></span>\
                <span class="svp_splash__video-live__text">Direkt</span>\
            </div>\
            <div class="svp_splash__inner">\
                <button class="' + BUTTON_ICONS.loading + ' ' + CLASSNAMES.playButton + ' ' + OVERRIDE_CLASSNAMES.playButton + '" \
                    aria-live="assertive" \
                    tabindex="0"\
                    data-rt="video-player-splash-play">\
                    <span class="svt_splash__video-icon--play"></span>\
                    <span class="svp_ui-controls__hidden">Spela video</span>\
                    <span class="svp_splash__text ' + CLASSNAMES.splashText + '"></span>\
                </button>\
            </div> \
        </div>'
    );

    if (SVP._cssNotLoadedClass) {
        domUtils.addClass(splashElement, SVP._cssNotLoadedClass);
        splashElement.style.visibility = 'hidden';
    }

    return splashElement;
}

function getVideoElement(api) {
    return Sizzle('video', api.element())[0];
}

function getSplashTextFromVideoElement(videoElement) {
    const splashText = videoAttributesUtil.getSplashText(videoElement);

    return splashText ? String(splashText) : '';
}

class SplashPlugin {
    constructor(api) {
        this.api = api;

        this.state = {
            isLive: false,
            status: STATUS.idle,
            wasPlayed: false
        };

        // Create DOM elements
        this.splashRootElement = createSplashElement();
        this.splashButtonElement = Sizzle('.' + CLASSNAMES.playButton, this.splashRootElement)[0];
        this.splashTextElement = Sizzle('.' + CLASSNAMES.splashText, this.splashRootElement)[0];

        this._addOwnEventListeners();
        this._addAPIEventListeners();
        this._addAPIFilters();

        this.updateElements();
        this.api.element().appendChild(this.splashRootElement);
    }

    setState(newState) {
        Debug.log('New splash state: ', newState);
        this.state = newState;
        this.updateElements();
    }

    mergeState(newState) {
        this.setState(Object.assign({}, this.state, newState));
    }

    /**
     * Update the DOM with the current state.
     */
    updateElements() {
        const { status, isLive } = this.state;
        const splashText = getSplashTextFromVideoElement(getVideoElement(this.api));
        const buttonIcon = status === STATUS.loading ? BUTTON_ICONS.loading : BUTTON_ICONS.play;

        // Update button
        domUtils.removeClass(this.splashButtonElement, BUTTON_ICONS.play);
        domUtils.removeClass(this.splashButtonElement, BUTTON_ICONS.loading);
        domUtils.addClass(this.splashButtonElement, buttonIcon);

        // Update text
        this.splashTextElement.innerHTML = splashText;
        domUtils.toggleClass(this.splashButtonElement, ATTRIBUTES.splashTextVisible, Boolean(splashText));

        // Update root
        domUtils.toggleClass(this.splashRootElement, STATE_CLASS.hidden, status === STATUS.hidden);
        domUtils.toggleClass(this.splashRootElement, ATTRIBUTES.videoIsLive, isLive);
    }

    /**
     * Attempt to play the video if it is ready, or load the video if it
     * is not yet loaded.
     */
    loadOrPlay() {
        const apiState = this.api.state();
        if (!apiState.ready && !apiState.loading) {
            // Load video if not yet loaded.
            this.api.load(this.api.data().videoId, true);
        } else {
            this.api.play().catch(() => {
                // If the play fails we are no longer loading.
                this.mergeState({ status: STATUS.idle });
            });
        }

    }

    destroy() {
        this.api.off(NAMESPACE + '.ready');
        this.api.off(NAMESPACE + '.playing');
        this.api.off(NAMESPACE + '.metadata');
        this.api.off(NAMESPACE + '.click');

        if (this.splashRootElement && this.splashRootElement.parentElement) {
            this.splashRootElement.parentElement.removeChild(
                this.splashRootElement
            );
        }
    }

    /**
     * @private
     */
    _addOwnEventListeners() {
        this.splashRootElement.addEventListener('keyup', (ev) => {
            if (this.state.status === STATUS.idle) {
                const key = domUtils.getKeyFromKeyEvent(ev);
                if (key === 'enter' || key === 'space') {
                    this.loadOrPlay();
                }
            }
        });
    }

    /**
     * @private
     */
    _addAPIEventListeners() {
        this.api.on(NAMESPACE + '.click', () => {
            if (this.state.status === STATUS.idle) {
                this.loadOrPlay();
            }
        });

        this.api.on(NAMESPACE + '.playing', () => {
            this.mergeState({
                status: STATUS.hidden,
                wasPlayed: true
            });
        });

        this.api.on(NAMESPACE + '.canplay', () => {
            if (!this.state.wasPlayed) {
                this.mergeState({
                    status: STATUS.idle
                });
            }
        });

        this.api.on(NAMESPACE + '.ready', () => {
            if (this.api.isAutoplay() && !this.state.wasPlayed) {
                this.mergeState({ status: STATUS.loading });
            } else {
                this.mergeState({ status: STATUS.idle });
            }
        });

        this.api.on(NAMESPACE + '.metadata', (ev) => {
            const isLive = Boolean(ev && ev.detail && ev.detail.live);

            this.mergeState({ isLive });
        });

        this.api.on(NAMESPACE + '.autoplaynotallowed', () => {
            this.mergeState({ status: STATUS.idle });
        });

        this.api.once(NAMESPACE + '.suspend', () => {
            if (!this.state.wasPlayed) {
                this.mergeState({ status: STATUS.idle });
            }
        });
    }

    /**
     * @private
     */
    _addAPIFilters() {
        this.api.filters.push({
            load: (videoId, autoplay) => {
                if (this.api.isAutoplay()) {
                    this.mergeState({ status: STATUS.loading, wasPlayed: false });
                } else {
                    this.mergeState({ status: STATUS.idle, wasPlayed: false });
                }
            },
            play: () => {
                if (!this.state.wasPlayed) {
                    this.mergeState({ status: STATUS.loading });
                }
            }
        });
    }
}

const instances = new Map();

export { NAMESPACE as namespace };

export function create(api) {
    if (api.config.autostart || !config.splash) {
        return;
    }

    if (instances.has(api)) {
        Debug.warn(
            'SplashPlugin was already created for this api instance and has' +
            ' not been destroyed. The previous instance may leak memory and' +
            ' listeners.'
        );
    }

    const instance = new SplashPlugin(api);
    instances.set(api, instance);

    return instance;
}

export function destroy(api) {
    if (instances.has(api)) {
        instances.get(api).destroy();
        instances.delete(api);
    }
}




// WEBPACK FOOTER //
// ./src/js/plugins/splash.js